import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore, CollectionReference, Query } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { combineLatest, forkJoin, map, Observable, of, switchMap } from 'rxjs';

import { CallableFunction, Collection } from '../resources/app-firebase';
import {
  CreateBookmarkProblemListRequest,
  CreateBookmarkProblemListResponse,
  Visibility,
  VisibilityType
} from '../models/create-bookmark-problem-list';
import { BookmarkProblemList, BookmarkProblemListTableData } from '../models/bookmark-problem-list';
import { User } from '../models/user';
import { Bookmark } from '../models/bookmark';
import { SUBJECT_NAMES } from '../resources/config';
import {
  AddProblemToBookmarkProblemListRequest,
  AddProblemToBookmarkProblemListResponse
} from '../models/add-problem-to-bookmark-problem-list';
import {
  DeleteProblemFromBookmarkProblemListRequest,
  DeleteProblemFromBookmarkProblemListResponse
} from '../models/delete-problem-from-bookmark-problem-list';
import { FindBookmarkProblemListProblemIdsRequest } from '../models/find-bookmark-problem-list-problem-ids-request';
import { UpdateBookmarkProblemListRequest, UpdateBookmarkProblemListResponse } from '../models/update-bookmark-problem-list';
import { DeleteBookmarkProblemListResponse } from '../models/delete-bookmark-problem-list';
import { Problems } from '../models/problem';
import { CopyBookmarkProblemListResponse } from '../models/copy-bookmark-problem-list';
import { ReorderBookmarkProblemListProblemsRequest } from '../models/reorder-bookmark-problem-list-problems';

@Injectable({
  providedIn: 'root'
})
export class BookmarkProblemListService {
  constructor(private afAuth: AngularFireAuth, private afs: AngularFirestore, private aff: AngularFireFunctions) {}

  findMyBookmarkProblemLists(userId: string, subjectIds: string[]): Observable<BookmarkProblemListTableData[]> {
    // subjectIdsが空の場合、リストを取得できないため、空配列を返却
    if (!subjectIds || subjectIds.length === 0) {
      return of([]);
    }

    const uncategorizedList$: Observable<BookmarkProblemListTableData[]> = forkJoin(
      subjectIds.map(subjectId => {
        // 未分類のリストの取得 - bookmarkコレクションより、問題数を取得
        return this.afs
          .collection<Bookmark>(Collection.BOOKMARK, ref => {
            let query: CollectionReference | Query = ref;
            query = query.where('userId', '==', userId);
            return query;
          })
          .get()
          .pipe(
            map(snapshot => {
              const initializedUncategorizedList = {
                id: `uncategorized_${subjectId}`,
                name: `未分類のリスト（${SUBJECT_NAMES[subjectId]}）`,
                userId,
                subjectId,
                visibility: VisibilityType.UNCATEGORIZED,
                visibilityLabel: '共有できません',
                schoolId: 'schoolId',
                problemCount: 0,
                folderId: null,
                createdAt: 'null'
              };

              return snapshot.empty
                ? (initializedUncategorizedList as BookmarkProblemListTableData)
                : ({
                    ...initializedUncategorizedList,
                    problemCount: snapshot.docs[0].data().problems.filter(problem => problem.subjectId === subjectId).length
                  } as BookmarkProblemListTableData);
            })
          );
      })
    );

    // お気に入りリストの取得 - bookmarkProblemListsコレクションより、該当のリストを取得
    const bookmarkProblemLists$ = this.afs
      .collection<BookmarkProblemList>(Collection.BOOKMARK_PROBLEM_LISTS, ref => {
        let query: CollectionReference | Query = ref;
        query = query.where('userId', '==', userId);
        return query;
      })
      .get()
      .pipe(
        map(snapshot =>
          snapshot.empty
            ? []
            : snapshot.docs.map(doc => {
                const bookmarkProblemListId: string = doc.id;
                const data = doc.data();
                data.id = bookmarkProblemListId;
                return data as BookmarkProblemListTableData;
              })
        )
      );

    // 未分類のリストとお気に入りリストの結合
    const combined$ = combineLatest([uncategorizedList$, bookmarkProblemLists$]).pipe(
      map(([uncategorizedList, bookmarkProblemLists]) => [...uncategorizedList, ...bookmarkProblemLists])
    );

    return combined$;
  }

  findSharedBookmarkProblemLists(schoolId: string, userId: string): Observable<BookmarkProblemListTableData[]> {
    // 同じ団体内で共有されているリストを取得
    const bookmarkProblemLists$ = this.afs
      .collection<BookmarkProblemList>(Collection.BOOKMARK_PROBLEM_LISTS, ref => {
        let query: CollectionReference | Query = ref;
        query = query.where('schoolId', '==', schoolId).where('visibility', '==', VisibilityType.SCHOOL_ONLY);
        return query;
      })
      .get()
      .pipe(
        map(snapshot =>
          snapshot.empty
            ? []
            : snapshot.docs
                .filter(doc => doc.data().userId !== userId) // 自分が共有したリストは省く
                .map(doc => {
                  const bookmarkProblemListId: string = doc.id;
                  const data = doc.data();
                  data.id = bookmarkProblemListId;
                  return data as BookmarkProblemListTableData;
                })
        )
      );

    // 同じ団体内で共有されているリストを取得
    const sharedBookmarkProblemLists$ = combineLatest([bookmarkProblemLists$]).pipe(
      switchMap(([bookmarkProblemLists]) =>
        bookmarkProblemLists.length === 0
          ? of([] as BookmarkProblemList[]) // 共有されているリストが存在しない場合は空の配列を返却
          : this.combineBookmarkProblemListsAndUserName(schoolId, userId, bookmarkProblemLists$)
      )
    );

    return sharedBookmarkProblemLists$;
  }

  findBookmarkProblemListProblemIds(bookmarkProblemListId: string): Observable<string[]> {
    const req: FindBookmarkProblemListProblemIdsRequest = { bookmarkProblemListId };
    const callable = this.aff.httpsCallable(CallableFunction.FIND_BOOKMARK_PROBLEM_LIST_PROBLEM_IDS);
    return callable(req);
  }

  addProblemToBookmarkProblemList(bookmarkProblemListId: string, problemId: string): Observable<AddProblemToBookmarkProblemListResponse> {
    const req: AddProblemToBookmarkProblemListRequest = { bookmarkProblemListId, problemId };
    const callable = this.aff.httpsCallable(CallableFunction.ADD_PROBLEM_TO_BOOKMARK_PROBLEM_LIST);
    return callable(req);
  }

  createBookmarkProblemList(subjectId: string, name: string, visibility: Visibility): Observable<CreateBookmarkProblemListResponse> {
    const req: CreateBookmarkProblemListRequest = { subjectId, name, visibility };
    const callable = this.aff.httpsCallable(CallableFunction.CREATE_BOOKMARK_PROBLEM_LIST);
    return callable(req);
  }

  updateBookmarkProblemList(id: string, name: string, visibility: Visibility): Observable<UpdateBookmarkProblemListResponse> {
    const req: UpdateBookmarkProblemListRequest = { id, name, visibility };
    const callable = this.aff.httpsCallable(CallableFunction.UPDATE_BOOKMARK_PROBLEM_LIST);
    return callable(req);
  }

  copyBookmarkProblemList(id: string): Observable<CopyBookmarkProblemListResponse> {
    const req = { id };
    const callable = this.aff.httpsCallable(CallableFunction.COPY_BOOKMARK_PROBLEM_LIST);
    return callable(req);
  }

  deleteBookmarkProblemList(id: string): Observable<DeleteBookmarkProblemListResponse> {
    const req = { id };
    const callable = this.aff.httpsCallable(CallableFunction.DELETE_BOOKMARK_PROBLEM_LIST);
    return callable(req);
  }

  deleteProblemFromBookmarkProblemList(
    bookmarkProblemListId: string,
    problemId: string
  ): Observable<DeleteProblemFromBookmarkProblemListResponse> {
    const req: DeleteProblemFromBookmarkProblemListRequest = { bookmarkProblemListId, problemId };
    const callable = this.aff.httpsCallable(CallableFunction.DELETE_PROBLEM_FROM_BOOKMARK_PROBLEM_LIST);
    return callable(req);
  }

  private combineBookmarkProblemListsAndUserName(
    schoolId: string,
    userId: string,
    bookmarkProblemLists$: Observable<BookmarkProblemList[]>
  ): Observable<BookmarkProblemList[]> {
    // 該当のお気に入りリストに紐づくUserIdを抽出
    const userIds$ = this.afs
      .collection<BookmarkProblemList>(Collection.BOOKMARK_PROBLEM_LISTS, ref => {
        let query: CollectionReference | Query = ref;
        query = query.where('schoolId', '==', schoolId).where('visibility', '==', VisibilityType.SCHOOL_ONLY);
        return query;
      })
      .get()
      .pipe(
        map(snapshot =>
          snapshot.empty
            ? []
            : snapshot.docs
                .filter(doc => doc.data().userId !== userId) // 自分が共有したリストは省く
                .map(doc => {
                  const data = doc.data();
                  const userId = data.userId;
                  return userId;
                })
        )
      );

    // 該当のリストに紐づくユーザー情報を取得
    const users$ = combineLatest([userIds$]).pipe(
      switchMap(([userIds]) => {
        const result$ = this.afs
          .collection<User>(Collection.USER, ref => {
            let query: CollectionReference | Query = ref;
            query = query.where('id', 'in', userIds);
            return query;
          })
          .get()
          .pipe(
            map(snapshot =>
              snapshot.empty
                ? []
                : snapshot.docs.map(doc => {
                    const user = doc.data();
                    return user as User;
                  })
            )
          );
        return result$;
      })
    );

    const listsWithOwnerName$ = combineLatest([users$, bookmarkProblemLists$]).pipe(
      map(([users, bookmarkProblemLists]) => {
        return bookmarkProblemLists.map(bookmarkProblemList => {
          const user = users.find(user => user.id === bookmarkProblemList.userId);
          return {
            ...bookmarkProblemList,
            ownerName: user ? `${user.familyName} ${user.firstName}` : ''
          } as BookmarkProblemListTableData;
        });
      })
    );

    return listsWithOwnerName$;
  }

  findBookmarkProblemListProblems(bookmarkProblemListId: string): Observable<Problems> {
    const req = { bookmarkProblemListId };
    const callable = this.aff.httpsCallable(CallableFunction.FIND_BOOKMARK_PROBLEM_LIST_PROBLEMS);
    return callable(req);
  }

  reorderBookmarkProblemListProblems(bookmarkProblemListId: string, problemIds: string[]): Observable<{ success: boolean }> {
    console.log('API call');
    const req: ReorderBookmarkProblemListProblemsRequest = { bookmarkProblemListId, problemIds };
    const callable = this.aff.httpsCallable(CallableFunction.RE_ORDER_BOOKMARK_PROBLEM_LIST_PROBLEMS);
    return callable(req);
  }

  findBookmarkProblemListProblemImages(bookmarkProblemListId: string): Observable<Problems> {
    const req = { bookmarkProblemListId };
    const callable = this.aff.httpsCallable(CallableFunction.FIND_BOOKMARK_PROBLEM_LIST_PROBLEM_IMAGES);
    return callable(req);
  }
}
